import { useLocation, Link, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/Breadcrumbs";
import { CITIES, findCity } from "../data/cities";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Layout";
import { siteUrl } from "../lib/siteUrl";
import { getNearestCities, getWaterHardness } from "../lib/cityServiceContent";
import { renderInline, stripMarkdown } from "../lib/inlineMarkdown";
import FaqAccordion from "../components/sections/FaqAccordion";
import PageFinalCta from "../components/sections/PageFinalCta";
import RelatedGeoPannes from "../components/sections/RelatedGeoPannes";
import ImageGallery from "../components/sections/ImageGallery";
import { TRUST_IMAGES } from "../data/images";
import CityServicesGrid from "../components/sections/CityServicesGrid";
import { getCityVoiceFaq } from "../data/voice-faq";
import { VoiceFaqSection } from "../components/home/HomeSections";
import CityInfoCards from "../components/sections/CityInfoCards";

/**
 * URL pattern : /reparateur-electromenager-{citySlug}
 * Page "hub ville" : pour les requêtes Google sans service spécifique
 * (ex. "réparateur électroménager Boulogne") — couverture des arrondissements
 * et communes individuellement, ciblant la proximité géographique.
 */

const SERVICES_GRID = [
    {
        key: "lave-linge",
        slugPrefix: "reparateur-lave-linge",
        display: "Lave-linge",
        icon: "fa-solid fa-shirt",
        desc: "Fuite, tambour bloqué, ne chauffe plus, code erreur : diagnostic à domicile et réparation toutes marques.",
        brands: "Bosch, Samsung, LG, Whirlpool, Miele, Siemens, Electrolux",
    },
    {
        key: "lave-vaisselle",
        slugPrefix: "reparateur-lave-vaisselle",
        display: "Lave-vaisselle",
        icon: "fa-solid fa-utensils",
        desc: "Vidange, vaisselle sale, fuite, code E15 : intervention sur appareils encastrés sans rayer les façades.",
        brands: "Bosch, Siemens, Miele, Smeg, Whirlpool, AEG",
    },
    {
        key: "frigo",
        slugPrefix: "reparateur-frigo",
        display: "Frigo & congélateur",
        icon: "fa-solid fa-snowflake",
        desc: "Plus de froid, ventilateur HS, fuite de gaz, glaçons bouchés : 12 causes diagnostiquées sur place.",
        brands: "Liebherr, Bosch, Samsung, LG, Miele, Whirlpool",
    },
    {
        key: "four",
        slugPrefix: "reparation-four",
        display: "Four & micro-ondes",
        icon: "fa-solid fa-fire",
        desc: "Pyrolyse, catalyse, vapeur : résistance, carte électronique, joint de porte, sécurité de fermeture.",
        brands: "Bosch, Siemens, Neff, Miele, Smeg, AEG, De Dietrich",
    },
    {
        key: "seche-linge",
        slugPrefix: "reparation-seche-linge",
        display: "Sèche-linge",
        icon: "fa-solid fa-wind",
        desc: "Pompe à chaleur, condensation, évacuation : nettoyage condenseur, sonde, capteur d'humidité, carte.",
        brands: "Miele, Bosch, Siemens, AEG, LG, Whirlpool",
    },
];

function getCityHubParagraphs(city) {
    const water = getWaterHardness(city);
    return [
        `Vous cherchez un **réparateur électroménager à ${city.name}** ? Nos techniciens interviennent à domicile dans toute la commune (${city.postal}) et ses quartiers — ${city.neighborhoods.slice(0, 5).join(", ")} — pour réparer lave-linge, lave-vaisselle, frigo, four et sèche-linge. Délai d'intervention : 24 à 48 h, urgences possibles le jour même.`,
        `À ${city.name} (${city.positionHabitat}), le parc immobilier est composé de ${city.habitat}. Cela implique souvent de l'électroménager **encastré** dans des cuisines équipées, des installations en colonne (lave-linge + sèche-linge superposés) et des contraintes d'accès propres au quartier (${city.access}). Nos techniciens connaissent ces spécificités et arrivent équipés pour intervenir efficacement, du rez-de-chaussée au dernier étage sans ascenseur de service.`,
        `Spécificité ${city.dept} : l'eau de ${city.name} est ${water.label} (${water.note}). C'est la première cause de pannes prématurées sur les appareils utilisant de l'eau (lave-linge, lave-vaisselle, frigo avec distributeur). Lors de chaque intervention, nous vérifions systématiquement l'état de la résistance et de la pompe, et conseillons un détartrage régulier adapté à la dureté locale.`,
    ];
}

function buildLocalFaqs(city, water) {
    return [
        {
            q: `Quel est le tarif d'un réparateur électroménager à ${city.name} ?`,
            a: `**Devis gratuit par téléphone**. Main-d'œuvre forfaitaire à partir de 99 €, annoncée avant intervention. Devis ferme sur place. Voir notre [barème complet](/blog/prix-reparation-electromenager-2026) ou la page [Tarifs](/tarifs).`,
        },
        {
            q: `En combien de temps intervenez-vous à ${city.name} ?`,
            a: `**24 à 48 heures** dans la grande majorité des cas, intervention **le jour même** pour les urgences (fuite, frigo HS). Nous couvrons l'ensemble de ${city.name} : ${city.neighborhoods.slice(0, 4).join(", ")}.`,
        },
        {
            q: `Quelles marques d'électroménager réparez-vous à ${city.name} ?`,
            a: `**Toutes les marques du marché** : Bosch, Siemens, Neff, Miele, LG, Samsung, Whirlpool, Electrolux, AEG, Beko, Smeg, Liebherr, De Dietrich, Brandt et plus. Voir notre classement de fiabilité : [Marques les plus fiables 2026](/blog/marques-electromenager-fiables-2026).`,
        },
        {
            q: `L'eau de ${city.name} abîme-t-elle mon électroménager ?`,
            a: `Oui : ${water.note} en ${city.dept}, ce qui entartre rapidement les résistances et pompes des lave-linge et lave-vaisselle. Nous recommandons un détartrage trimestriel à l'acide citrique. Méthode complète : [Détartrer son lave-linge](/blog/detartrer-lave-linge-methode).`,
        },
        {
            q: `Intervenez-vous sur les appareils encastrés à ${city.name} ?`,
            a: `Oui, sans rayure ni dommage sur les meubles. Le parc immobilier de ${city.name} (${city.habitat}) est très majoritairement équipé d'électroménager intégré, nos techniciens démontent et remontent les façades sur mesure.`,
        },
        {
            q: `La réparation est-elle garantie à ${city.name} ?`,
            a: `Oui, **garantie 18 mois** pièces et main-d'œuvre. Si la même panne réapparaît dans ce délai, nous revenons gratuitement. Détails : [page FAQ](/faq#garantie).`,
        },
    ];
}

function buildJsonLd({ crumbs, city, localFaqs, voiceFaq, pathname }) {
    const url = siteUrl(pathname);
    return {
        "@context": "https://schema.org",
        "@graph": [
            breadcrumbJsonLd(crumbs),
            {
                "@type": "WebPage",
                "@id": `${url}#webpage`,
                "url": url,
                "name": `Réparateur électroménager ${city.name}`,
                "inLanguage": "fr-FR",
                "speakable": {
                    "@type": "SpeakableSpecification",
                    "cssSelector": [".speakable-headline", ".speakable-voice-faq"],
                },
            },
            {
                "@type": "LocalBusiness",
                "@id": siteUrl(`${pathname}#localbusiness`),
                "name": `Electrocase – Réparateur électroménager ${city.name}`,
                "image": siteUrl("/og-image.jpg"),
                "telephone": PHONE_TEL.replace("tel:", ""),
                "priceRange": "€€",
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": city.name,
                    "postalCode": city.postal,
                    "addressCountry": "FR",
                },
                "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": city.coords.lat,
                    "longitude": city.coords.lon,
                },
                "areaServed": city.name,
            },
            {
                "@type": "FAQPage",
                "mainEntity": [...localFaqs.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": stripMarkdown(f.a) },
                })), ...voiceFaq.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": f.a },
                }))],
            },
        ],
    };
}

export default function CityHubPage() {
    const location = useLocation();
    const citySlug = location.pathname.replace(/^\/reparateur-electromenager-/, "");
    const city = findCity(citySlug);
    if (!city) return <Navigate to="/zones-intervention" replace />;

    const url = siteUrl(location.pathname);
    const water = getWaterHardness(city);
    const paragraphs = getCityHubParagraphs(city);
    const neighbors = getNearestCities(city.slug, 6);

    const crumbs = [
        { name: "Accueil", to: "/" },
        { name: "Zones d'intervention", to: "/zones-intervention" },
        { name: city.name },
    ];

    const localFaqs = buildLocalFaqs(city, water);
    const voiceFaq = getCityVoiceFaq(city);
    const jsonLd = buildJsonLd({ crumbs, city, localFaqs, voiceFaq, pathname: location.pathname });

    return (
        <>
            <Seo
                title={`Réparateur électroménager ${city.name} – 24 h | Electrocase`}
                description={`Réparateur électroménager à ${city.name}, toutes marques — lave-linge, frigo, lave-vaisselle, four. Sous 24-48 h, garantie 18 mois. ☎ ${PHONE_DISPLAY}`}
                keywords={`réparateur électroménager ${city.name}, dépannage électroménager ${city.name}, réparation lave-linge ${city.name}, réparation frigo ${city.name}, ${city.postal}`}
                canonical={url}
                jsonLd={jsonLd}
            />

            {/* HERO */}
            <section className="bg-[var(--paper)] border-b border-[var(--line)]">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
                    <Breadcrumbs items={crumbs} />
                    <div className="mt-6 grid lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8">
                            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--navy)]/80">
                                <i className="fa-solid fa-location-dot text-[var(--amber)]" /> {city.dept} · {city.postal}
                            </p>
                            <h1 className="heading text-4xl lg:text-5xl xl:text-6xl font-extrabold mt-4 leading-[1.05] speakable-headline">
                                Réparateur électroménager à {city.name}
                            </h1>
                            <p className="mt-5 text-lg text-[var(--ink-soft)] max-w-2xl">
                                Intervention à domicile <strong>sous 24 à 48 h</strong> sur lave-linge, lave-vaisselle, frigo, four et sèche-linge — toutes marques. devis gratuit par téléphone, garantie 18 mois.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <a href={PHONE_TEL} className="btn-amber inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold" data-testid="hero-call-btn">
                                    <i className="fa-solid fa-phone-volume" /> Appeler {PHONE_DISPLAY}
                                </a>
                                <Link to="/contact" className="btn-outline-navy inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
                                    Devis gratuit en ligne
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-4">
                            <div className="card-soft p-5 bg-white">
                                <p className="heading font-bold text-[var(--navy)] flex items-center gap-2">
                                    <i className="fa-solid fa-circle-info text-[var(--amber)]" /> Zone d&apos;intervention
                                </p>
                                <p className="text-sm text-[var(--ink-soft)] mt-3 leading-relaxed">
                                    Tous les quartiers de {city.name} : {city.neighborhoods.join(", ")}.
                                </p>
                                <p className="text-sm text-[var(--ink-soft)] mt-3">
                                    <i className="fa-solid fa-train-subway text-[var(--amber)] mr-2" />{city.access}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTEXTE LOCAL */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-2xl lg:text-3xl font-extrabold">Pourquoi choisir Electrocase à {city.name} ?</h2>
                <div className="mt-6 grid lg:grid-cols-3 gap-6 text-[var(--ink-soft)] leading-relaxed">
                    {paragraphs.map((p, i) => (
                        <p key={`hub-para-${p.slice(0, 30)}-${i}`}>{renderInline(p)}</p>
                    ))}
                </div>
            </section>

            <CityServicesGrid city={city} services={SERVICES_GRID} />

            {city.slug.startsWith("paris-") && (
                <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-4">
                    <Link
                        to={`/reparation-electromenager-${city.slug}`}
                        data-testid="city-reparation-link"
                        className="group flex flex-wrap items-center justify-between gap-4 border-2 border-[var(--amber)] rounded-2xl p-6 bg-[var(--paper)] hover:bg-white transition"
                    >
                        <div>
                            <h2 className="text-lg md:text-lg font-black text-[var(--navy)]">
                                Réparation électroménager {city.name} — guide complet
                            </h2>
                            <p className="text-sm text-[var(--ink-soft)] mt-1">
                                Tous les appareils réparables dans le {city.slug.replace("paris-", "") === "1" ? "1er" : `${city.slug.replace("paris-", "")}ᵉ`}, quartiers couverts et questions fréquentes.
                            </p>
                        </div>
                        <span className="font-black text-[var(--navy)] group-hover:text-[var(--amber)] transition">Voir la page →</span>
                    </Link>
                </section>
            )}

            <CityInfoCards cityName={city.name} />

            <VoiceFaqSection items={voiceFaq} />

            <FaqAccordion
                title={`FAQ – Réparateur électroménager ${city.name}`}
                intro={`Les questions les plus fréquentes des habitants de ${city.name}.`}
                faqs={localFaqs}
                renderAnswer={renderInline}
                testIdPrefix="city-faq"
                bgClassName="bg-[var(--paper)] border-y border-[var(--line)]"
            />

            {/* VILLES VOISINES */}
            {neighbors.length > 0 && (
                <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
                    <div className="divider-amber mb-5" />
                    <h2 className="heading text-2xl lg:text-3xl font-extrabold">Réparateur électroménager dans les villes voisines</h2>
                    <p className="mt-3 text-[var(--ink-soft)]">Communes les plus proches que nous couvrons également.</p>
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {neighbors.map((n) => (
                            <Link key={n.slug} to={`/reparateur-electromenager-${n.slug}`} className="card-soft p-4 block group" data-testid={`neighbor-${n.slug}`}>
                                <i className="fa-solid fa-location-dot text-[var(--amber)]" />
                                <p className="heading font-bold text-sm mt-2 group-hover:text-[var(--navy)]">{n.name}</p>
                                <p className="text-xs text-[var(--ink-soft)]">{n.postal} · {Math.round(n._distKm)} km</p>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <PageFinalCta
                eyebrow={`Besoin d'un dépannage à ${city.name} ?`}
                title="Appelez maintenant, intervention sous 24 à 48 h."
                subtitle="Devis gratuit par téléphone, technicien dépêché le jour même selon planning."
                secondaryHref="/contact"
            />

            <RelatedGeoPannes cityHubLink={location.pathname} />

            <ImageGallery
                title={`Réparation électroménager à ${city.name} — En images`}
                images={TRUST_IMAGES}
            />
        </>
    );
}

/* Helper for App.js + sitemap generator */
export function allCityHubPaths() {
    return CITIES.map((c) => `/reparateur-electromenager-${c.slug}`);
}
